import { useEffect } from "react";

import { Button, Grid2, Typography } from "@mui/material";
import classNames from "classnames";
import { useForm, useWatch } from "react-hook-form";

import { validators } from "../../../../utils";
import { CommonInput } from "../../../common";
import { useTeaching } from "../hooks/useTeaching";

export type TSummaryFieldValues = {
	airHours: string;
	simulatorHours: string;
	milesFlown: string;
	flightSummary: string;
};

export function Summary() {
	const { currentBooking, updateStudentProgress } = useTeaching();

	const { control, handleSubmit, reset } = useForm<TSummaryFieldValues>({
		defaultValues: {
			airHours: (currentBooking?.airHours ?? "").toString(),
			simulatorHours: (currentBooking?.simulatorHours ?? "").toString(),
			milesFlown: (currentBooking?.milesFlown ?? "").toString(),
			flightSummary: currentBooking?.flightSummary ?? "",
		},
	});

	const { airHours, simulatorHours, milesFlown, flightSummary } = useWatch({
		control,
	});

	useEffect(() => {
		reset({
			airHours: (currentBooking?.airHours ?? "").toString(),
			simulatorHours: (currentBooking?.simulatorHours ?? "").toString(),
			milesFlown: (currentBooking?.milesFlown ?? "").toString(),
			flightSummary: currentBooking?.flightSummary ?? "",
		});
	}, [
		currentBooking?.airHours,
		currentBooking?.flightSummary,
		currentBooking?.milesFlown,
		currentBooking?.simulatorHours,
		reset,
	]);

	if (!currentBooking) {
		return null;
	}

	const onClickCancel = () => {
		reset({
			airHours: (currentBooking?.airHours ?? "").toString(),
			simulatorHours: (currentBooking?.simulatorHours ?? "").toString(),
			milesFlown: (currentBooking?.milesFlown ?? "").toString(),
			flightSummary: currentBooking?.flightSummary ?? "",
		});
	};

	const onClickSaveSummary = (data: TSummaryFieldValues) => {
		updateStudentProgress(currentBooking.id, {
			airHours: Number(data.airHours),
			simulatorHours: Number(data.simulatorHours),
			milesFlown: Number(data.milesFlown),
			flightSummary: data.flightSummary.trim(),
		});
	};

	const calculateIsDisabled = () => {
		if (Number(airHours) !== currentBooking?.airHours) {
			return false;
		}

		if (Number(simulatorHours) !== currentBooking?.simulatorHours) {
			return false;
		}

		if (Number(milesFlown) !== currentBooking?.milesFlown) {
			return false;
		}

		if (flightSummary?.trim() !== currentBooking?.flightSummary.trim()) {
			return false;
		}

		return true;
	};

	const isDisabled = calculateIsDisabled();

	return (
		<div className="summary-container item-separator-big">
			<form onSubmit={handleSubmit(onClickSaveSummary)}>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
						<Typography className="normal-text bold">Air Hours</Typography>
						<div className="item-separator-tiny" />
						<CommonInput
							control={control}
							placeholder="Air Hours"
							name="airHours"
							rules={{
								pattern: {
									value: validators.number,
									message: "",
								},
							}}
							disallowInvalidInput
							suffix="hrs"
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
						<Typography className="normal-text bold">
							Simulator Hours
						</Typography>
						<div className="item-separator-tiny" />
						<CommonInput
							control={control}
							placeholder="Simulator Hours"
							name="simulatorHours"
							rules={{
								pattern: {
									value: validators.number,
									message: "",
								},
							}}
							disallowInvalidInput
							suffix="hrs"
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
						<Typography className="normal-text bold">Miles Flown</Typography>
						<div className="item-separator-tiny" />
						<CommonInput
							control={control}
							placeholder="Miles Flown"
							name="milesFlown"
							rules={{
								pattern: {
									value: validators.number,
									message: "",
								},
							}}
							disallowInvalidInput
							suffix="mi"
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Typography className="normal-text bold">Follow-up</Typography>
						<div className="item-separator-tiny" />
						<CommonInput
							control={control}
							placeholder="Students love feedback, tips, and personal assignments. What can you pass on to the student to accelerate learning?"
							name="flightSummary"
							multiline
						/>
					</Grid2>
				</Grid2>
				<div
					className="item-separator-small"
					style={{
						display: "flex",
						gap: "16px",
						justifyContent: "flex-end",
						alignItems: "center",
					}}>
					<Button
						className="summary-button normal-button disabled-button"
						onClick={onClickCancel}>
						Cancel
					</Button>
					<Button
						className={classNames("summary-button normal-button", {
							"primary-button": !isDisabled,
							"disabled-button": isDisabled,
						})}
						type="submit"
						disabled={isDisabled}>
						Save
					</Button>
				</div>
			</form>
		</div>
	);
}
